import { Form } from 'antd';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled, { css } from 'styled-components';
import { isObjectNullOrEmpty } from 'utils/helpers';

const Quill = styled(ReactQuill)`
  width: ${p => p.width || '500px'};
  height: ${p => p.height || '250px'};
  background-color: #fff;
  .ql-tooltip .ql-action {
    display: none;
  }
  strong {
    font-weight: bold !important;
  }

  .ql-container {
    height: calc(100% - 42px);
  }

  ${p => p.readOnly && css`
    background-color: rgba(0, 0, 0, 0.04) !important;
    cursor: not-allowed !important;

    & * {
      cursor: not-allowed !important;
      pointer-events: none !important;
    }
  `}

  ${p => p.status === 'error' && css`
    .ql-toolbar, .ql-container {
      border: 1px solid #ff4d4f;
    }

    .ql-toolbar {
      border-bottom:  1px solid #ccc;
    }
  `}
`;

const PopUp = styled.ul`
    position: absolute;
    width: max-content;
    top: ${p => p.location.top + (p.height - 20) || 20}px;
    left: ${p => p.location.left || 0}px;
    background: white;
    border: 1px solid #D7D8D9;
    border-radius: 5px;
    list-style-type: none;
    padding: 5px;
    z-index: 100;
    li {
      &:hover {
        background: rgba(0, 0, 0, 0.04);
      }
      cursor: pointer;
      display: flex;
      align-items: center;
      height: 30px;
      border-bottom: 1px solid #D7D8D9;
    }
    li:last-child {
      border-bottom: none;
    }
`;

const Wrapper = styled.div`
    position: relative;
`;

const RichTextEditor = ({
  width,
  height,
  value = '',
  onChange,
  disabled,
  placeholder = '',
  keyBindings = {},
}) => {
  // Antd will pass an onChange func, we can use that to upstream the value of the input
  // and have the value returned by antd Form getFieldValue
  const { status } = Form.Item.useStatus();
  const [popUpInfo, setPopUpInfo] = useState({});
  const inputRef = useRef();

  const triggerChange = (changedValue) => {
    if (onChange) {
      // https://github.com/quilljs/quill/issues/1328
      // if you clear everything an empty line break will persist -> input never empty
      if (changedValue === '<p><br></p>') {
        onChange('');
      } else {
        onChange(changedValue);
      }
    }
  };

  const handleKeyBinds = (e) => {
    if (!isObjectNullOrEmpty(keyBindings) && keyBindings[e.key]) {
      const binding = keyBindings[e.key];
      const editor = inputRef?.current?.editor;
      setPopUpInfo({
        ...binding,
        location: {
          ...editor?.getBounds(editor?.selection?.savedRange?.index),
          index: editor?.selection?.savedRange?.index,
        },
      });
    } else {
      setPopUpInfo({});
    }
  };

  const handlePopUpInfoClick = (t) => {
    const editor = inputRef?.current?.editor;
    if (editor) {
      const text = editor.clipboard?.convert(
        // eslint-disable-next-line max-len
        `<a href='${window.location.origin}/${t.type}/${t.objectId}?context=${t.objectId}' target='_blank'>${t.description}</a>`,
      );

      const before = editor.getContents(0, popUpInfo.location.index);
      const after = editor.getContents(popUpInfo.location.index + 1, editor.getLength());
      const newDelta = before
        .concat(text)
        .concat(after);
      editor.setContents(newDelta, 'silent');

      if (popUpInfo.cb) {
        popUpInfo.cb(t.objectId);
      }
    }
    setPopUpInfo({});
  };
  const insertActions = isToggleEnabled('allowImagesInTextInput') ? ['link', 'image'] : ['link'];

  return (
    <Wrapper data-test-id='rich-text-editor'>
      <Quill
        ref={inputRef}
        theme='snow'
        value={value}
        onChange={triggerChange}
        width={width}
        height={height}
        readOnly={disabled}
        status={status}
        placeholder={placeholder}
        onKeyPress={handleKeyBinds}
        modules={{
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline'],
            insertActions,
            [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
            ['clean'],
          ],
        }}
        onBlur={() => setTimeout(() => setPopUpInfo({}), 300)}
        onChangeSelection={range =>
          !isObjectNullOrEmpty(popUpInfo) &&
          !isObjectNullOrEmpty(range) &&
          range?.index - 1 !== popUpInfo?.location?.index &&
          setTimeout(() => setPopUpInfo({}), 300)}
      />
      {!isObjectNullOrEmpty(popUpInfo) ?
        <PopUp
          location={popUpInfo.location}
          height={20 * popUpInfo.value?.length}
        >
          {popUpInfo.value?.map(t =>
            <li id={t.objectId} key={t.objectId} onClick={() => handlePopUpInfoClick(t)}>
              <span className='tags-nav-span'>
                <span className='tags-nav-span-child'>
                  <i className={`fa fa-${t.icon}`} />{' '}
                  {t.type}:
                </span>
                <span>
                  {t.description.includes(`${t.type}:`) ?
                    t.description.replace(`${t.type}:`, '') :
                    t.description
                  }
                </span>
              </span>
            </li>,
          )}
        </PopUp > :
        null
      }
    </Wrapper>
  );
};

RichTextEditor.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  keyBindings: PropTypes.object,
};

export default RichTextEditor;
